import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import Supercluster from 'supercluster';
import { useTranslation } from 'react-i18next';
import { AreaIcon, BathroomIcon, BedroomIcon } from '../../../components/base/icons/MetricIcons';
import SaudiRiyalSymbol from '../../../components/base/icons/SaudiRiyalSymbol';
import { formatAmount, latnLocale } from '../../../utils/numberFormat';
import type { Property } from '../../../mocks/properties';
import { loadGoogleMaps, getGoogleMapsApiKey, getGoogleMapsMapId, getMapColorScheme, didGoogleMapsAuthFail } from '../../../utils/googleMapsLoader';
import { fetchAreaBoundaries, fetchAreaInsights, geojsonToPaths, scoreTint, type AreaInsights, type AreaBoundary } from '../../../services/neighborhoodMapService';
import NeighborhoodInsightsPanel from './NeighborhoodInsightsPanel';
import { filterByRegion, regionBoundsPoints, type DrawnRegion } from '../../../utils/mapRegion';

const GOOGLE_MAPS_API_KEY = getGoogleMapsApiKey();

// Novel map features kept hidden from external reviewers until launch. Default
// OFF — each only appears when its env flag is explicitly "true". To work on
// them locally, add VITE_DISTRICT_INSIGHTS_ENABLED=true /
// VITE_DISTRICT_BOUNDARY_ENABLED=true to your gitignored Apps/web/.env;
// production / review builds leave them unset => the features stay hidden.
const DISTRICT_INSIGHTS_ENABLED = import.meta.env?.VITE_DISTRICT_INSIGHTS_ENABLED === 'true';
const DISTRICT_BOUNDARY_ENABLED = import.meta.env?.VITE_DISTRICT_BOUNDARY_ENABLED === 'true';

// Western digits with "," grouping in both languages (utils/numberFormat.ts).
const formatRiyalNumber = (value: number) => formatAmount(value, { maximumFractionDigits: 0 });

// `locale` is a Latin-digit locale (see `locale` in MapView): «2.7 مليون» / "2.7M".
const formatCompactPrice = (value: number, locale: string) => {
  if (value >= 1000000) {
    return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  }
  if (value >= 1000) {
    return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 0 }).format(value);
  }
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
};

// Create styled price badge marker icon with house emoji
const createPriceMarkerIcon = (price: string, isSelected: boolean = false) => {
  const bgColor = isSelected ? '#004D40' : '#ffffff';
  const textColor = isSelected ? '#ffffff' : '#1D2B29';
  const borderColor = isSelected ? '#004D40' : '#E0E9E7';
  const shadowOpacity = isSelected ? '0.3' : '0.15';

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="56">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="${shadowOpacity}"/>
        </filter>
      </defs>
      <!-- House emoji at top -->
      <text x="40" y="16" text-anchor="middle" font-size="18">🏠</text>
      <!-- Price badge below -->
      <rect x="4" y="22" width="72" height="24" rx="12" ry="12" fill="${bgColor}" stroke="${borderColor}" stroke-width="1.5" filter="url(#shadow)"/>
      <text x="40" y="38" text-anchor="middle" font-family="'Tajawal', Arial, sans-serif" font-size="11" font-weight="600" fill="${textColor}">${price}</text>
      <!-- Pointer triangle -->
      <polygon points="40,46 35,54 45,54" fill="${bgColor}"/>
      <polyline points="35,46 40,53 45,46" fill="none" stroke="${borderColor}" stroke-width="1.5"/>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
};

// Create cluster marker icon showing count (accepts a supercluster
// abbreviated string like "4k" as well as a raw number).
const createClusterIcon = (count: number | string) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56">
      <defs>
        <filter id="clusterShadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="3" stdDeviation="4" flood-opacity="0.25"/>
        </filter>
      </defs>
      <!-- Circle background -->
      <circle cx="28" cy="28" r="24" fill="#004D40" stroke="white" stroke-width="3" filter="url(#clusterShadow)"/>
      <!-- House emoji -->
      <text x="28" y="22" text-anchor="middle" font-size="14">🏠</text>
      <!-- Count number -->
      <text x="28" y="38" text-anchor="middle" font-family="'Tajawal', Arial, sans-serif" font-size="14" font-weight="700" fill="white">${count}</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// AdvancedMarkerElement content builders (DOM elements replace the deprecated
// google.maps.Marker `icon`). The SVG generators above are reused verbatim — their
// data-URL output is just wrapped in an <img>.
function markerImg(src: string, w: number, h: number): HTMLImageElement {
  const img = document.createElement('img');
  img.src = src;
  img.width = w;
  img.height = h;
  img.style.cssText = 'display:block;cursor:pointer;';
  return img;
}

// Wrap content so its CENTER (not the default bottom-center) sits on the position,
// matching the old `anchor: Point(size/2, size/2)` used by clusters and vertex dots.
function centeredContent(el: HTMLElement): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.transform = 'translateY(50%)';
  wrap.appendChild(el);
  return wrap;
}

interface MapViewProps {
  properties: Property[];
  selectedProperty: Property | null;
  onPropertySelect: (property: Property) => void;
  /**
   * The region currently drawn elsewhere (i.e. persisted by the parent across a
   * grid↔map toggle). Used to rehydrate the circle/polygon when the map remounts
   * so the user's drawn area survives switching views.
   */
  region?: DrawnRegion;
  /** Reports the committed region up to the parent so grid view can filter by it. */
  onRegionChange?: (region: DrawnRegion) => void;
  /** When true, arm the polygon draw tool once the map is ready (used by the
   *  "Draw your region on the map" shortcut from the search bar). */
  autoDraw?: boolean;
  /** Called once the autoDraw request has been consumed, so the parent can reset
   *  the flag and not re-arm drawing on a later remount. */
  onAutoDrawConsumed?: () => void;
  /** The district selected via search: its boundary is outlined + framed, and
   *  its insights panel is shown. The actual listing filter is server-side
   *  (areaId) — this is purely the visual focus. */
  selectedAreaBoundary?: AreaBoundary | null;
  selectedAreaInsights?: AreaInsights | null;
  selectedAreaLoading?: boolean;
  /** Closing the selected-district panel clears the district filter upstream. */
  onClearSelectedArea?: () => void;
}

const DEFAULT_CENTER = { lat: 21.4858, lng: 39.1925 };
const DEFAULT_ZOOM = 11;
const MARKER_WIDTH = 80;
const MARKER_HEIGHT = 56; // Taller to accommodate house emoji
const CLUSTER_SIZE = 56;

// Payload carried on each supercluster point feature — just enough to look the
// full Property back up (by id) when rendering an individual pin.
type ClusterPointProps = { propertyId: string };
// The settled camera we render against: viewport bbox [W,S,E,N] + integer zoom.
// Captured on the map's 'idle' event so we cluster/draw once the camera stops.
type MapViewport = { bbox: [number, number, number, number]; zoom: number };

export default function MapView({ properties, selectedProperty, onPropertySelect, region, onRegionChange, autoDraw, onAutoDrawConsumed, selectedAreaBoundary, selectedAreaInsights, selectedAreaLoading, onClearSelectedArea }: MapViewProps) {
  const { t, i18n } = useTranslation(['search', 'property', 'common']);
  const [hoveredProperty, setHoveredProperty] = useState<Property | null>(null);
  const [clickedProperty, setClickedProperty] = useState<Property | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The settled viewport (bbox + zoom), updated on map 'idle'. Marker rendering
  // keys off this so we only ever draw what's on screen, and only after the
  // camera stops - never mid-pan/zoom animation (which caused the freeze).
  const [mapView, setMapView] = useState<MapViewport | null>(null);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const clusterMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Draw-radius search: a draggable + resizable circle that filters properties to
  // those inside it.
  // Drawing state is seeded from the `region` prop so a region drawn before the
  // user switched to grid view is restored when the map remounts.
  const circleRef = useRef<google.maps.Circle | null>(null);
  const [radiusActive, setRadiusActive] = useState(() => region?.kind === 'radius');
  const [radiusCenter, setRadiusCenter] = useState<{ lat: number; lng: number } | null>(
    () => (region?.kind === 'radius' ? region.center : null),
  );
  const [radiusMeters, setRadiusMeters] = useState<number>(
    () => (region?.kind === 'radius' ? region.radiusMeters : 3000),
  );

  // Draw-polygon search: a free-form area the user sketches around any region.
  // Implemented with plain map click listeners + a live Polyline (see effect
  // below) instead of google.maps.drawing.DrawingManager - the built-in tool
  // silently no-ops when the drawing library isn't loaded with the right
  // cursor stylesheet, leaving the user stuck on grab-to-pan.
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  // 'idle' = no polygon UI, 'drawing' = waiting for user to click points,
  // 'done' = polygon committed and filtering active.
  const [polygonState, setPolygonState] = useState<'idle' | 'drawing' | 'done'>(
    () => (region?.kind === 'polygon' ? 'done' : 'idle'),
  );
  const [polygonPath, setPolygonPath] = useState<google.maps.LatLngLiteral[]>(
    () => (region?.kind === 'polygon' ? region.path : []),
  );

  // Toggles the "properties in this region" list panel, opened from the (i)
  // button on the radius/polygon count badge.
  const [showRegionList, setShowRegionList] = useState(false);

  // One-shot: arm the polygon draw tool when the parent requests it (the search
  // bar's "Draw your region on the map" shortcut). Mirrors togglePolygon's
  // idle→drawing path; inlined to avoid a forward reference to togglePolygon.
  const autoDrawDoneRef = useRef(false);
  useEffect(() => {
    if (!autoDraw || !isMapReady || autoDrawDoneRef.current) return;
    autoDrawDoneRef.current = true;
    setRadiusActive(false);
    setRadiusCenter(null);
    setShowRegionList(false);
    setPolygonPath([]);
    setPolygonState('drawing');
    onAutoDrawConsumed?.();
  }, [autoDraw, isMapReady, onAutoDrawConsumed]);

  // Neighborhood borders: stored Area boundary polygons, toggled on the map.
  // Clicking one opens a curated insights panel (sales, views, type mix).
  const neighborhoodPolygonsRef = useRef<google.maps.Polygon[]>([]);
  // The highlighted outline of the district chosen via search (separate from the
  // all-neighborhoods toggle above).
  const selectedAreaPolygonsRef = useRef<google.maps.Polygon[]>([]);
  const [showNeighborhoods, setShowNeighborhoods] = useState(false);
  const [activeInsights, setActiveInsights] = useState<AreaInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // Debounced hover to prevent rapid state updates when moving mouse across markers
  const setHoveredPropertyDebounced = useCallback((property: Property | null) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (property) {
      hoverTimeoutRef.current = setTimeout(() => setHoveredProperty(property), 150);
    } else {
      hoverTimeoutRef.current = setTimeout(() => setHoveredProperty(null), 300);
    }
  }, []);

  // Arabic words («ألف» / «مليون») where Intl adds them, but always Latin digits
  // (numerals policy, utils/numberFormat.ts) — never «٢٫٧ مليون».
  const locale = latnLocale(i18n.language?.startsWith('ar') ? 'ar' : 'en-US');
  const direction = i18n.dir();
  const unitLabel = t('common:units.sqm');
  const currencyName = t('common:currency.name');

  const propertyTranslations = useMemo(() => {
    const data = t('property:properties', { returnObjects: true, defaultValue: {} }) as Record<
      string,
      Record<string, unknown>
    >;
    return data ?? {};
  }, [t, i18n.language]);

  const getLocalizedField = useCallback(
    (property: Property, key: string, fallback: string): string => {
      const override = propertyTranslations[property.id];
      if (override && typeof override[key] === 'string' && (override[key] as string).length > 0) {
        return override[key] as string;
      }
      return fallback;
    },
    [propertyTranslations],
  );

  const formatArea = useCallback((sqft: number): string => formatAmount(Math.round(sqft / 10.7639)), []);

  const { centerLat, centerLng, zoom } = useMemo(() => {
    if (properties.length === 0) {
      return { centerLat: DEFAULT_CENTER.lat, centerLng: DEFAULT_CENTER.lng, zoom: DEFAULT_ZOOM };
    }

    const avgLat = properties.reduce((sum, property) => sum + property.latitude, 0) / properties.length;
    const avgLng = properties.reduce((sum, property) => sum + property.longitude, 0) / properties.length;

    const latValues = properties.map((property) => property.latitude);
    const lngValues = properties.map((property) => property.longitude);
    const latSpread = Math.max(...latValues) - Math.min(...latValues);
    const lngSpread = Math.max(...lngValues) - Math.min(...lngValues);
    const maxSpread = Math.max(latSpread, lngSpread);

    let calculatedZoom = 12;
    if (maxSpread > 10) calculatedZoom = 6;
    else if (maxSpread > 5) calculatedZoom = 8;
    else if (maxSpread > 1) calculatedZoom = 10;
    else if (maxSpread > 0.1) calculatedZoom = 12;
    else calculatedZoom = 14;

    return { centerLat: avgLat, centerLng: avgLng, zoom: calculatedZoom };
  }, [properties]);

  const cleanupMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current = [];
    // Also cleanup cluster markers
    clusterMarkersRef.current.forEach((marker) => {
      marker.map = null;
    });
    clusterMarkersRef.current = [];
  }, []);

  const handleMarkerClick = useCallback(
    (property: Property) => {
      onPropertySelect(property);
      setHoveredProperty(null);
    },
    [onPropertySelect],
  );

  const getPropertyTypeLabel = useCallback(
    (type: string) => {
      const typeLabels: Record<string, { en: string; ar: string }> = {
        apartment: { en: 'Apartment', ar: 'شقة' },
        villa: { en: 'Villa', ar: 'فيلا' },
        townhouse: { en: 'Townhouse', ar: 'تاون هاوس' },
        penthouse: { en: 'Penthouse', ar: 'بنتهاوس' },
        duplex: { en: 'Duplex', ar: 'دوبلكس' },
        land: { en: 'Land', ar: 'أرض' },
        office: { en: 'Office', ar: 'مكتب' },
        shop: { en: 'Shop', ar: 'محل' },
        warehouse: { en: 'Warehouse', ar: 'مستودع' },
        building: { en: 'Building', ar: 'عمارة' },
      };
      const label = typeLabels[type?.toLowerCase()];
      return label ? (i18n.language === 'ar' ? label.ar : label.en) : type;
    },
    [i18n.language],
  );

  // Initialize the map
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      setError('Google Maps API key not configured');
      setIsMapReady(false);
      return;
    }

    let isMounted = true;

    async function initMap() {
      try {
        await loadGoogleMaps();

        if (!isMounted || !mapRef.current) return;

        const position = { lat: centerLat, lng: centerLng };

        // Create the map
        const mapInstance = new google.maps.Map(mapRef.current, {
          center: position,
          zoom,
          // mapTypeControl off — Google's Satellite/Terrain toggle anchors
          // to the top-left and overlapped the in-app "N listings" badge.
          // Property search rarely needs satellite view; can be re-enabled
          // and moved (mapTypeControlOptions.position) if requested later.
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: false, // We'll add custom zoom controls
          // Required for AdvancedMarkerElement. With a Map ID, POI-label hiding
          // moves to cloud-based map styles, so the inline `styles` option is dropped.
          mapId: getGoogleMapsMapId(),
          // Render the map dark in dark mode (vector map + Map ID required).
          colorScheme: getMapColorScheme(),
        });

        mapInstanceRef.current = mapInstance;

        // Re-cluster/re-render markers whenever the camera settles (pan or zoom).
        // Using 'idle' — not 'zoom_changed' — means we rebuild ONCE after motion
        // stops, off the settled viewport, instead of many times mid-animation.
        const captureViewport = () => {
          // getBounds is absent from this @types/google.maps version (same reason
          // as the setOptions casts elsewhere); it exists at runtime. Read the
          // bbox via toJSON() → {west,south,east,north} to avoid the LatLng
          // .lat()/.lng() method-vs-property ambiguity in these types.
          const bounds = (mapInstance as unknown as {
            getBounds: () => { toJSON: () => { west: number; south: number; east: number; north: number } } | undefined;
          }).getBounds();
          const z = mapInstance.getZoom();
          if (!bounds || z === undefined) return;
          const { west, south, east, north } = bounds.toJSON();
          setMapView({ bbox: [west, south, east, north], zoom: Math.round(z) });
        };
        google.maps.event.addListener(mapInstance, 'idle', captureViewport);

        // Clear clicked property when clicking empty map area
        google.maps.event.addListener(mapInstance, 'click', () => {
          setClickedProperty(null);
        });

        // Create shared info window
        infoWindowRef.current = new google.maps.InfoWindow();

        setIsMapReady(true);
        setError(null);
      } catch (err) {
        console.error('Error initializing map:', err);
        if (isMounted) {
          setError('Failed to load map');
          setIsMapReady(false);
        }
      }
    }

    initMap();

    return () => {
      isMounted = false;
      cleanupMarkers();
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, [centerLat, centerLng, zoom, cleanupMarkers]);

  // When returning to the map from grid view with a region already drawn, frame
  // it once so the user immediately sees the shape they sketched (otherwise the
  // map opens centred on all results and the rehydrated shape can be off-screen).
  const didFitRegionRef = useRef(false);
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !isMapReady || didFitRegionRef.current) return;
    didFitRegionRef.current = true;
    if (!region) return;

    const points = regionBoundsPoints(region);
    if (points.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 32);
  }, [isMapReady, region]);

  // The committed search region, derived from whichever draw tool is active. An
  // in-progress polygon ('drawing') is deliberately not a region yet. This is
  // the single source of truth for both the on-map filter and what we report up.
  const currentRegion = useMemo<DrawnRegion>(() => {
    if (radiusActive && radiusCenter) {
      return { kind: 'radius', center: radiusCenter, radiusMeters };
    }
    if (polygonState === 'done' && polygonPath.length >= 3) {
      return { kind: 'polygon', path: polygonPath };
    }
    return null;
  }, [radiusActive, radiusCenter, radiusMeters, polygonState, polygonPath]);

  // Lift the committed region to the parent so the grid/list view and its
  // mini-map preview stay in sync with what was drawn here. `onRegionChange`
  // (setState from the parent) is stable, so this never loops.
  useEffect(() => {
    onRegionChange?.(currentRegion);
  }, [currentRegion, onRegionChange]);

  // Properties shown on the map: all of them, or only those inside the drawn
  // region (radius via Haversine, polygon via ray-casting — see utils/mapRegion,
  // which is shared with the grid view so both stay perfectly in sync).
  const visibleProperties = useMemo(
    () => filterByRegion(properties, currentRegion),
    [properties, currentRegion],
  );

  // Spatial index over the (region-filtered) properties. Built ONCE per data
  // change — panning/zooming just queries it per viewport, which is what makes
  // a 4000-point country-wide map cheap: getClusters() returns only what's on
  // screen at the current zoom (a few dozen features), never all 4000.
  const clusterIndex = useMemo(() => {
    const index = new Supercluster<ClusterPointProps>({
      radius: 60, // px cluster radius
      maxZoom: 16, // beyond this, always show individual pins
      minPoints: 2,
    });
    index.load(
      visibleProperties.map((p) => ({
        type: 'Feature' as const,
        properties: { propertyId: p.id },
        geometry: { type: 'Point' as const, coordinates: [p.longitude, p.latitude] },
      })),
    );
    return index;
  }, [visibleProperties]);

  // id → Property, so a clustered point feature can be resolved back to its full
  // record when we build its pin (O(1), avoids a .find over all properties).
  const propertyById = useMemo(() => {
    const map = new Map<string, Property>();
    visibleProperties.forEach((p) => map.set(p.id, p));
    return map;
  }, [visibleProperties]);

  // Create markers for the CURRENT VIEWPORT only, via the supercluster index.
  //
  // The old version tore down and rebuilt a marker for every property in the
  // whole dataset on every zoom tick — so clicking a 4000-point cluster froze
  // the tab for seconds. Now we ask the index for just the clusters/points inside
  // the settled viewport at the current zoom (getClusters → a few dozen features),
  // so the number of DOM markers is bounded by what's on screen, not by dataset
  // size. Reruns are cheap, so panning/zooming/selection all stay smooth.
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const infoWindow = infoWindowRef.current;

    if (!mapInstance || !isMapReady || !mapView || !window.google?.maps) {
      return;
    }

    cleanupMarkers();

    // Set up global handler for popup button clicks
    window.selectProperty = (propertyId: string) => {
      const property = propertyById.get(propertyId) ?? properties.find((item) => item.id === propertyId);
      if (property) {
        handleMarkerClick(property);
      }
    };

    // Only what's visible at the settled zoom. Each feature is either a cluster
    // (properties.cluster === true, carrying point_count + cluster_id) or a single
    // point (carrying our propertyId).
    const clusters = clusterIndex.getClusters(mapView.bbox, mapView.zoom);

    clusters.forEach((feature) => {
      const [lng, lat] = feature.geometry.coordinates;
      const props = feature.properties;

      if ('cluster' in props && props.cluster) {
        // Cluster bubble — click zooms to exactly where it breaks apart.
        const count = props.point_count;
        const label = props.point_count_abbreviated ?? count;
        const clusterContent = centeredContent(markerImg(createClusterIcon(label), CLUSTER_SIZE, CLUSTER_SIZE));
        const clusterMarker = new google.maps.marker.AdvancedMarkerElement({
          position: { lat, lng },
          map: mapInstance,
          title: `${count} properties`,
          content: clusterContent,
          zIndex: 500,
        });

        clusterMarker.addListener('gmp-click', () => {
          // getClusterExpansionZoom = the minimum zoom at which this cluster
          // splits. One setZoom + panTo — no fitBounds over-zoom/bounce, no dump
          // of hundreds of pins. 'idle' then re-queries the index at the new zoom.
          const expansionZoom = Math.min(clusterIndex.getClusterExpansionZoom(props.cluster_id), 18);
          mapInstance.setZoom(expansionZoom);
          (mapInstance as unknown as { panTo: (latLng: google.maps.LatLngLiteral) => void }).panTo({ lat, lng });
        });

        // Change cursor on hover to indicate clickable cluster.
        // setOptions exists on google.maps.Map at runtime but is missing
        // from some @types/google.maps versions; cast to bypass.
        clusterContent.addEventListener('mouseenter', () => {
          (mapInstance as unknown as { setOptions: (opts: { draggableCursor: string }) => void }).setOptions({ draggableCursor: 'pointer' });
        });
        clusterContent.addEventListener('mouseleave', () => {
          (mapInstance as unknown as { setOptions: (opts: { draggableCursor: string }) => void }).setOptions({ draggableCursor: '' });
        });

        clusterMarkersRef.current.push(clusterMarker);
      } else {
        // Single property — resolve the full record and show the price pin.
        const property = propertyById.get((props as ClusterPointProps).propertyId);
        if (!property) return;

        const priceLabel = formatCompactPrice(property.price, locale);
        const isSelected = selectedProperty?.id === property.id;

        const content = markerImg(createPriceMarkerIcon(priceLabel, isSelected), MARKER_WIDTH, MARKER_HEIGHT);
        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: { lat, lng },
          map: mapInstance,
          title: getLocalizedField(property, 'title', property.title),
          content, // bottom-center anchor matches the old Point(W/2, H); no DROP animation in AdvancedMarker
          zIndex: isSelected ? 1000 : 1,
        });

        // Store property reference on marker for later access
        (marker as unknown as { propertyData: Property }).propertyData = property;

        marker.addListener('gmp-click', () => {
          // Close any open InfoWindow
          if (infoWindow) {
            infoWindow.close();
          }
          setClickedProperty(property);
          setHoveredProperty(property);
          // Update all individual markers to reflect selection
          markersRef.current.forEach((m) => {
            const prop = (m as unknown as { propertyData: Property }).propertyData;
            if (prop) {
              const isNowSelected = prop.id === property.id;
              (m.content as HTMLImageElement).src = createPriceMarkerIcon(formatCompactPrice(prop.price, locale), isNowSelected);
              m.zIndex = isNowSelected ? 1000 : 1;
            }
          });
        });

        content.addEventListener('mouseenter', () => {
          setHoveredPropertyDebounced(property);
          content.src = createPriceMarkerIcon(priceLabel, true);
          marker.zIndex = 999;
        });

        content.addEventListener('mouseleave', () => {
          setHoveredPropertyDebounced(null);
          const isStillSelected = selectedProperty?.id === property.id;
          content.src = createPriceMarkerIcon(priceLabel, isStillSelected);
          marker.zIndex = isStillSelected ? 1000 : 1;
        });

        markersRef.current.push(marker);
      }
    });

    return () => {
      window.selectProperty = undefined;
    };
  }, [clusterIndex, propertyById, mapView, properties, isMapReady, cleanupMarkers, handleMarkerClick, getLocalizedField, locale, selectedProperty, setHoveredPropertyDebounced]);

  // Draw-radius circle lifecycle: create/destroy the draggable+editable circle as
  // the user toggles radius mode, and keep React state in sync with edits/drags.
  //
  // CRITICAL: the deps deliberately exclude radiusMeters/radiusCenter. Including
  // them caused this bug: when the user resized the circle, the sync handler
  // called setRadiusMeters() → React re-rendered → this effect's cleanup ran
  // and clearInstanceListeners() killed the circle's own listeners. Every
  // subsequent drag/resize then failed to update React state, so the filter
  // kept comparing against the original (default) center. The visible circle
  // moved, the filter didn't follow, and the count showed 0.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !isMapReady) return;

    if (!radiusActive) {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      return;
    }

    if (circleRef.current) return; // already drawn

    const mapCenter = map.getCenter();
    const fallbackCenter = mapCenter
      ? { lat: mapCenter.lat(), lng: mapCenter.lng() }
      : DEFAULT_CENTER;
    // Reuse the rehydrated center/radius when returning from grid view; only
    // fall back to the map centre + 3 km default for a fresh activation.
    const startCenter = radiusCenter ?? fallbackCenter;
    const startRadius = radiusMeters || 3000;

    const circle = new google.maps.Circle({
      map,
      center: startCenter,
      radius: startRadius,
      editable: true,
      draggable: true,
      strokeColor: '#004D40',
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: '#004D40',
      fillOpacity: 0.12,
    });
    circleRef.current = circle;
    if (!radiusCenter) setRadiusCenter(startCenter);

    const sync = () => {
      const c = circle.getCenter();
      if (c) setRadiusCenter({ lat: c.lat(), lng: c.lng() });
      setRadiusMeters(Math.round(circle.getRadius()));
    };
    circle.addListener('radius_changed', sync);
    circle.addListener('center_changed', sync);
    circle.addListener('dragend', sync);

    // eslint-disable-next-line consistent-return
    return () => {
      google.maps.event.clearInstanceListeners(circle);
      circle.setMap(null);
      circleRef.current = null;
    };
    // radiusCenter/radiusMeters are READ here only to seed the circle on a fresh
    // activation or a rehydration mount; they're intentionally excluded from the
    // deps (see the comment above) so the effect doesn't tear the circle down on
    // every drag/resize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radiusActive, isMapReady]);

  // Polygon drawing — FREEHAND ("pen") implementation.
  //
  // Earlier this was click-to-drop-vertices, which felt like placing dots
  // rather than drawing. Now the user presses and drags to trace the region
  // freehand (mouse or touch), and releasing closes the area. Implemented
  // with DOM pointer events on the map container + an OverlayView projection
  // to convert screen pixels → lat/lng (no DrawingManager, which Google
  // removed in Maps JS v3.65).
  //
  // Behaviour:
  //  - Press and drag anywhere on the map to draw; a live purple line follows
  //    the cursor/finger.
  //  - Release to finish. Need ≥3 points (a real drag) — a stray tap resets.
  //  - While drawing, panning/zoom gestures are disabled and the cursor is a
  //    crosshair; everything is restored on cleanup.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !isMapReady) return;

    if (polygonState === 'idle') {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
      return;
    }

    if (polygonState !== 'drawing') return;

    const container = (map as unknown as { getDiv: () => HTMLElement }).getDiv();
    const points: google.maps.LatLngLiteral[] = [];
    let isDrawing = false;
    let lastPx: { x: number; y: number } | null = null;

    const polyline = new google.maps.Polyline({
      map,
      path: [],
      strokeColor: '#004D40',
      strokeOpacity: 0.95,
      strokeWeight: 3,
      clickable: false,
    });

    // Headless overlay purely to expose the pixel↔latLng projection.
    const overlay = new google.maps.OverlayView();
    overlay.onAdd = () => {};
    overlay.draw = () => {};
    overlay.onRemove = () => {};
    overlay.setMap(map);

    // Disable all map gestures while drawing so a drag traces instead of pans.
    const mapWithSetOptions = map as unknown as {
      setOptions: (opts: Record<string, unknown>) => void;
    };
    mapWithSetOptions.setOptions({
      draggable: false,
      gestureHandling: 'none',
      draggableCursor: 'crosshair',
      disableDoubleClickZoom: true,
      scrollwheel: false,
    });
    const prevTouchAction = container.style.touchAction;
    container.style.touchAction = 'none';

    const toLatLng = (clientX: number, clientY: number): google.maps.LatLngLiteral | null => {
      const projection = overlay.getProjection();
      if (!projection) return null;
      const rect = container.getBoundingClientRect();
      const pixel = new google.maps.Point(clientX - rect.left, clientY - rect.top);
      const ll = projection.fromContainerPixelToLatLng(pixel);
      return ll ? { lat: ll.lat(), lng: ll.lng() } : null;
    };

    const finish = () => {
      if (points.length >= 3) {
        setPolygonPath(points.slice());
        setPolygonState('done');
      } else {
        // Too small to be a real area (a tap, not a drag) — reset and let
        // the user try again without leaving draw mode.
        points.length = 0;
        polyline.setPath([]);
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button && e.button !== 0) return; // primary button / touch only
      e.preventDefault();
      isDrawing = true;
      points.length = 0;
      lastPx = { x: e.clientX, y: e.clientY };
      try { container.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      const ll = toLatLng(e.clientX, e.clientY);
      if (ll) { points.push(ll); polyline.setPath(points); }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDrawing) return;
      e.preventDefault();
      // Throttle by min screen distance (~6px) so the path stays light.
      if (lastPx) {
        const dx = e.clientX - lastPx.x;
        const dy = e.clientY - lastPx.y;
        if (dx * dx + dy * dy < 36) return;
      }
      const ll = toLatLng(e.clientX, e.clientY);
      if (ll) {
        points.push(ll);
        polyline.setPath(points);
        lastPx = { x: e.clientX, y: e.clientY };
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!isDrawing) return;
      isDrawing = false;
      try { container.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      finish();
    };

    container.addEventListener('pointerdown', onPointerDown);
    container.addEventListener('pointermove', onPointerMove, { passive: false });
    container.addEventListener('pointerup', onPointerUp);
    container.addEventListener('pointercancel', onPointerUp);

    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('pointercancel', onPointerUp);
      container.style.touchAction = prevTouchAction;
      polyline.setMap(null);
      overlay.setMap(null);
      mapWithSetOptions.setOptions({
        draggable: true,
        gestureHandling: 'auto',
        draggableCursor: null,
        disableDoubleClickZoom: false,
        scrollwheel: true,
      });
    };
  }, [polygonState, isMapReady]);

  // Once 'done', render a filled polygon from the committed path so the
  // user sees the area they drew.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !isMapReady) return;

    if (polygonState !== 'done' || polygonPath.length < 3) {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
      return;
    }

    const poly = new google.maps.Polygon({
      map,
      paths: polygonPath,
      fillColor: '#004D40',
      fillOpacity: 0.12,
      strokeColor: '#004D40',
      strokeOpacity: 0.9,
      strokeWeight: 2,
      clickable: false,
    });
    polygonRef.current = poly;

    return () => {
      poly.setMap(null);
      if (polygonRef.current === poly) polygonRef.current = null;
    };
  }, [polygonState, polygonPath, isMapReady]);

  // Handle selected property changes
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || !selectedProperty) {
      return;
    }

    mapInstance.setCenter({ lat: selectedProperty.latitude, lng: selectedProperty.longitude });
    mapInstance.setZoom(15);
  }, [selectedProperty]);

  const propertyCountLabel = useMemo(
    () => t('search.map.countLabel', { count: visibleProperties.length }),
    [visibleProperties.length, t],
  );

  const displayProperty = hoveredProperty || clickedProperty || selectedProperty;
  // The district-insights panel and the property preview card are both bottom-
  // anchored. This flag lets the card offset clear of the panel on desktop (and
  // paint on top on mobile, where they stack) so the panel never blocks it.
  const districtPanelOpen =
    !!selectedAreaInsights ||
    !!selectedAreaLoading ||
    (showNeighborhoods && (insightsLoading || !!activeInsights));
  const displayPriceValue = displayProperty ? formatRiyalNumber(displayProperty.price) : '';
  const displayPriceAccessible = displayProperty
    ? t('search.card.priceAccessible', { price: displayPriceValue, currency: currencyName })
    : '';

  // Position of the popup's property within the currently-visible list, used for
  // the "n / total" counter and to page forward/back through the same set.
  const displayIndex = displayProperty
    ? visibleProperties.findIndex((p) => p.id === displayProperty.id)
    : -1;

  // Step to the previous/next visible property without leaving the map. Drives
  // clickedProperty (the popup's own state, which overrides hover) and pans the
  // map to the new pin. Wraps around the ends.
  const navigateAdjacent = useCallback(
    (delta: number) => {
      if (visibleProperties.length === 0) return;
      const currentId = (clickedProperty || hoveredProperty || selectedProperty)?.id;
      const idx = currentId ? visibleProperties.findIndex((p) => p.id === currentId) : -1;
      const base = idx === -1 ? 0 : idx + delta;
      const len = visibleProperties.length;
      const next = visibleProperties[((base % len) + len) % len];
      setHoveredProperty(null);
      setClickedProperty(next);
      mapInstanceRef.current?.setCenter({ lat: next.latitude, lng: next.longitude });
    },
    [visibleProperties, clickedProperty, hoveredProperty, selectedProperty],
  );

  const handleZoomIn = () => {
    const mapInstance = mapInstanceRef.current;
    if (mapInstance) {
      mapInstance.setZoom((mapInstance.getZoom() || DEFAULT_ZOOM) + 1);
    }
  };

  const handleZoomOut = () => {
    const mapInstance = mapInstanceRef.current;
    if (mapInstance) {
      mapInstance.setZoom((mapInstance.getZoom() || DEFAULT_ZOOM) - 1);
    }
  };

  const toggleRadius = () => {
    setShowRegionList(false);
    setRadiusActive((prev) => {
      const next = !prev;
      if (!next) {
        // Turning off → clear filter state.
        setRadiusCenter(null);
      } else {
        // Turning on → recentre the circle on the current view + zoom to it.
        const map = mapInstanceRef.current;
        const c = map?.getCenter();
        if (c) setRadiusCenter({ lat: c.lat(), lng: c.lng() });
        // Mutually exclusive with polygon mode — clear any drawn area.
        setPolygonState('idle');
        setPolygonPath([]);
      }
      return next;
    });
  };

  // Polygon controls. The DrawingManager lifecycle effect below handles
  // attaching/detaching the drawing tool; this just toggles the mode state.
  const togglePolygon = () => {
    setShowRegionList(false);
    setPolygonState((prev) => {
      if (prev === 'idle') {
        // Mutually exclusive with radius — clear it if active.
        if (radiusActive) {
          setRadiusActive(false);
          setRadiusCenter(null);
        }
        return 'drawing';
      }
      // 'drawing' or 'done' → clear.
      setPolygonPath([]);
      return 'idle';
    });
  };

  const clearPolygon = () => {
    setPolygonState('idle');
    setPolygonPath([]);
  };

  const handleResetView = () => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance) return;

    if (properties.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      properties.forEach((property) => {
        bounds.extend({ lat: property.latitude, lng: property.longitude });
      });
      mapInstance.fitBounds(bounds);
    } else {
      mapInstance.setCenter(DEFAULT_CENTER);
      mapInstance.setZoom(DEFAULT_ZOOM);
    }
  };

  const toggleNeighborhoods = () => {
    setShowNeighborhoods((prev) => {
      if (prev) setActiveInsights(null);
      return !prev;
    });
  };

  // Draw / tear down the stored neighborhood boundary polygons.
  useEffect(() => {
    if (!isMapReady || !mapInstanceRef.current) return;
    const map = mapInstanceRef.current;
    let cancelled = false;

    const clear = () => {
      neighborhoodPolygonsRef.current.forEach((p) => p.setMap(null));
      neighborhoodPolygonsRef.current = [];
    };

    if (!DISTRICT_INSIGHTS_ENABLED || !showNeighborhoods) {
      clear();
      return;
    }

    fetchAreaBoundaries()
      .then((areas) => {
        if (cancelled || !mapInstanceRef.current) return;
        clear();
        areas.forEach((area) => {
          if (!area.boundary) return;
          const paths = geojsonToPaths(area.boundary);
          if (paths.length === 0) return;
          // Districts with a score are tinted by it (green→amber); unscored
          // districts keep the neutral violet wash — honest, nothing fabricated.
          const hasScore = area.score != null;
          const fillColor = hasScore ? scoreTint(area.score).fill : '#004D40';
          const baseOpacity = hasScore ? 0.18 : 0.08;
          const hoverOpacity = hasScore ? 0.32 : 0.22;
          const polygon = new google.maps.Polygon({
            paths,
            strokeColor: '#003930',
            strokeOpacity: 0.9,
            strokeWeight: 1.5,
            fillColor,
            fillOpacity: baseOpacity,
            clickable: true,
            map,
          });
          polygon.addListener('mouseover', () => polygon.setOptions({ fillOpacity: hoverOpacity }));
          polygon.addListener('mouseout', () => polygon.setOptions({ fillOpacity: baseOpacity }));
          polygon.addListener('click', () => {
            setInsightsLoading(true);
            setActiveInsights(null);
            fetchAreaInsights(area.areaId, '90d')
              .then((data) => { if (!cancelled) setActiveInsights(data); })
              .catch(() => { if (!cancelled) setActiveInsights(null); })
              .finally(() => { if (!cancelled) setInsightsLoading(false); });
          });
          neighborhoodPolygonsRef.current.push(polygon);
        });
      })
      .catch(() => { /* boundaries not available yet — silently skip */ });

    return () => { cancelled = true; clear(); };
  }, [showNeighborhoods, isMapReady]);

  // Outline + frame the district selected via search. Drawn as a stronger
  // highlight than the toggle borders, and the map fits to it once.
  useEffect(() => {
    if (!isMapReady || !mapInstanceRef.current) return;
    const map = mapInstanceRef.current;

    const clear = () => {
      selectedAreaPolygonsRef.current.forEach((p) => p.setMap(null));
      selectedAreaPolygonsRef.current = [];
    };
    clear();

    // Hidden from external reviewers until launch (VITE_DISTRICT_BOUNDARY_ENABLED):
    // skip drawing the outline + reframing, leaving only the plain base map.
    if (!DISTRICT_BOUNDARY_ENABLED) return;
    if (!selectedAreaBoundary?.boundary) return;
    const paths = geojsonToPaths(selectedAreaBoundary.boundary);
    if (paths.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    let extended = false;
    paths.forEach((ring) => ring.forEach((pt) => { bounds.extend(pt); extended = true; }));
    paths.forEach((ring) => {
      const polygon = new google.maps.Polygon({
        paths: ring,
        strokeColor: '#003930',
        strokeOpacity: 1,
        strokeWeight: 3,
        fillColor: '#004D40',
        fillOpacity: 0.12,
        clickable: false,
        map,
      });
      selectedAreaPolygonsRef.current.push(polygon);
    });
    if (extended) map.fitBounds(bounds, 48);

    return () => clear();
  }, [selectedAreaBoundary, isMapReady]);

  if (error) {
    const authFail = didGoogleMapsAuthFail();
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return (
      <div className="relative h-full w-full bg-gray-100 flex items-center justify-center p-6">
        <div className="text-center text-slate-500 max-w-md">
          <i className="ri-map-pin-line text-4xl mb-2"></i>
          <p className="text-sm font-medium">{error}</p>
          {authFail && (
            <p className="mt-2 text-xs text-slate-400 leading-relaxed">
              {direction === 'rtl'
                ? `مفتاح خرائط Google لا يسمح بهذا النطاق (${origin}). أضِفه في Google Cloud Console ← بيانات الاعتماد ← قيود الموقع، وتأكد من تفعيل "Maps JavaScript API" مع الفوترة.`
                : `The Google Maps key does not allow this origin (${origin}). Add it in Google Cloud Console → Credentials → Website restrictions, and ensure "Maps JavaScript API" is enabled with billing.`}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full bg-gray-100" dir={direction}>
      <div ref={mapRef} className="absolute inset-0 z-10" />

      {!isMapReady && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/85">
          <div className="text-center">
            <i className="ri-loader-4-line mb-4 text-4xl text-blue-600 animate-spin"></i>
            <p className="text-gray-600">{t('search.map.loading')}</p>
          </div>
        </div>
      )}

      <div className="absolute top-4 left-4 z-30 rounded-lg border border-gray-200 bg-white px-4 py-2 shadow-md" dir="ltr">
        <span className="text-sm font-medium text-gray-900">{propertyCountLabel}</span>
      </div>

      <div className="absolute top-4 right-4 z-30 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
        <button
          type="button"
          className="border-b border-gray-200 p-3 transition-colors hover:bg-gray-50"
          title={t('search.map.zoomIn')}
          onClick={handleZoomIn}
        >
          <i className="ri-zoom-in-line text-gray-600"></i>
        </button>
        <button
          type="button"
          className="border-b border-gray-200 p-3 transition-colors hover:bg-gray-50"
          title={t('search.map.zoomOut')}
          onClick={handleZoomOut}
        >
          <i className="ri-zoom-out-line text-gray-600"></i>
        </button>
        <button
          type="button"
          className="border-b border-gray-200 p-3 transition-colors hover:bg-gray-50"
          title={t('search.map.reset')}
          onClick={handleResetView}
        >
          <i className="ri-focus-3-line text-gray-600"></i>
        </button>
        <button
          type="button"
          className={`border-b border-gray-200 p-3 transition-colors ${radiusActive ? 'bg-violet-600 hover:bg-violet-700' : 'hover:bg-gray-50'}`}
          title={t('search.map.drawRadius', { defaultValue: direction === 'rtl' ? 'البحث ضمن دائرة' : 'Search within a radius' })}
          onClick={toggleRadius}
        >
          <i className={`ri-compasses-2-line ${radiusActive ? 'text-white' : 'text-gray-600'}`}></i>
        </button>
        <button
          type="button"
          className={`p-3 transition-colors ${polygonState !== 'idle' ? 'bg-violet-600 hover:bg-violet-700' : 'hover:bg-gray-50'}`}
          title={
            polygonState === 'drawing'
              ? (direction === 'rtl' ? 'اضغط واسحب لرسم منطقتك' : 'Press and drag to draw your area')
              : (direction === 'rtl' ? 'ارسم منطقة بنفسك' : 'Draw an area freehand')
          }
          onClick={togglePolygon}
        >
          <i className={`ri-pen-nib-line ${polygonState !== 'idle' ? 'text-white' : 'text-gray-600'}`}></i>
        </button>
        <button
          type="button"
          disabled={!DISTRICT_INSIGHTS_ENABLED}
          className={`border-t border-gray-200 p-3 transition-colors ${
            !DISTRICT_INSIGHTS_ENABLED
              ? 'cursor-not-allowed opacity-40'
              : showNeighborhoods
                ? 'bg-violet-600 hover:bg-violet-700'
                : 'hover:bg-gray-50'
          }`}
          title={
            !DISTRICT_INSIGHTS_ENABLED
              ? (direction === 'rtl' ? 'إحصاءات الأحياء - قريباً' : 'Neighborhood insights - coming soon')
              : (direction === 'rtl' ? 'حدود الأحياء' : 'Neighborhood borders')
          }
          onClick={DISTRICT_INSIGHTS_ENABLED ? toggleNeighborhoods : undefined}
        >
          <i className={`ri-shape-line ${DISTRICT_INSIGHTS_ENABLED && showNeighborhoods ? 'text-white' : 'text-gray-600'}`}></i>
        </button>
      </div>

      {/* District-score tint legend — only while borders are shown. Sits on a
          second row under the listing-count pill (which is hardcoded at
          top-4/left-4 and never flips), clear of the right-4 control column. */}
      {DISTRICT_INSIGHTS_ENABLED && showNeighborhoods && (
        <div
          className="absolute top-16 left-4 z-20 flex max-w-[calc(100%-88px)] flex-wrap items-center gap-2 rounded-full border border-gray-200 bg-white/95 px-3 py-1.5 text-[11px] text-gray-600 shadow-md"
          dir={direction}
        >
          <span className="font-medium">{t('search.location.districtScore', { defaultValue: 'District score' })}</span>
          <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: scoreTint(4.5).fill }} /> 4+</span>
          <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: scoreTint(3.7).fill }} /> 3.5+</span>
          <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: scoreTint(3.2).fill }} /> 3+</span>
          <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: scoreTint(null).fill }} /> {t('search.location.districtScoreNone', { defaultValue: 'no score' })}</span>
        </div>
      )}

      {/* District insights — shown for the searched/selected district. Takes
          precedence over the toggle panel. Closing it clears the district filter. */}
      {DISTRICT_INSIGHTS_ENABLED && (selectedAreaInsights || selectedAreaLoading) && (
        <NeighborhoodInsightsPanel
          insights={selectedAreaInsights ?? null}
          loading={!!selectedAreaLoading}
          direction={direction}
          locale={locale}
          onClose={() => onClearSelectedArea?.()}
        />
      )}

      {/* Neighborhood insights panel — opens when a toggle border is clicked */}
      {DISTRICT_INSIGHTS_ENABLED && !selectedAreaInsights && !selectedAreaLoading && showNeighborhoods && (insightsLoading || activeInsights) && (
        <NeighborhoodInsightsPanel
          insights={activeInsights}
          loading={insightsLoading}
          direction={direction}
          locale={locale}
          onClose={() => setActiveInsights(null)}
        />
      )}

      {/* Polygon hint / count / clear. Drawing state prompts the user; done
          state shows the in-area count + a Clear chip. */}
      {polygonState === 'drawing' && (
        <div
          className="absolute bottom-4 left-1/2 z-30 -translate-x-1/2 flex max-w-[90vw] items-center gap-3 rounded-full border border-violet-200 bg-white/95 px-4 py-2 shadow-lg"
          dir={direction}
        >
          <i className="ri-pen-nib-line text-violet-600 shrink-0"></i>
          <span className="text-sm font-medium text-violet-900">
            {direction === 'rtl'
              ? 'اضغط واسحب لرسم منطقتك · ارفع إصبعك للإنهاء'
              : 'Press and drag to draw your area · release to finish'}
          </span>
          <button
            type="button"
            onClick={clearPolygon}
            className="shrink-0 rounded-full bg-violet-100 px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-200"
          >
            {direction === 'rtl' ? 'إلغاء' : 'Cancel'}
          </button>
        </div>
      )}
      {polygonState === 'done' && (
        <div
          className="absolute bottom-4 left-1/2 z-30 -translate-x-1/2 flex items-center gap-3 rounded-full border border-violet-200 bg-white/95 px-4 py-2 shadow-lg"
          dir={direction}
        >
          <i className="ri-pencil-ruler-2-line text-violet-600"></i>
          <span className="text-sm font-semibold text-violet-900">
            {direction === 'rtl'
              ? `${visibleProperties.length} عقار في المنطقة المرسومة`
              : `${visibleProperties.length} in drawn area`}
          </span>
          {visibleProperties.length > 0 && (
            <button
              type="button"
              onClick={() => setShowRegionList((v) => !v)}
              className={`flex h-8 w-8 items-center justify-center rounded-full text-base font-bold ring-1 transition ${showRegionList ? 'bg-violet-600 text-white ring-violet-600' : 'bg-white text-violet-700 ring-violet-300 hover:bg-violet-50'}`}
              aria-label={direction === 'rtl' ? 'عرض العقارات في المنطقة' : 'View properties in area'}
              aria-pressed={showRegionList}
              title={direction === 'rtl' ? 'عرض العقارات في المنطقة' : 'View properties in area'}
            >
              <i className={showRegionList ? 'ri-eye-fill' : 'ri-eye-line'}></i>
            </button>
          )}
          <button
            type="button"
            onClick={clearPolygon}
            className="rounded-full bg-violet-100 px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-200"
          >
            {direction === 'rtl' ? 'مسح' : 'Clear'}
          </button>
        </div>
      )}

      {/* Radius-search hint + count + clear, shown while the circle is active. */}
      {radiusActive && (
        <div
          className="absolute bottom-4 left-1/2 z-30 -translate-x-1/2 flex items-center gap-3 rounded-full border border-violet-200 bg-white/95 px-4 py-2 shadow-lg"
          dir={direction}
        >
          <i className="ri-compasses-2-line text-violet-600"></i>
          <span className="text-sm font-semibold text-violet-900">
            {direction === 'rtl'
              ? `${visibleProperties.length} عقار ضمن ${(radiusMeters / 1000).toFixed(1)} كم`
              : `${visibleProperties.length} in ${(radiusMeters / 1000).toFixed(1)} km`}
          </span>
          <span className="hidden text-xs text-slate-400 sm:inline">
            {direction === 'rtl' ? 'اسحب الدائرة أو غيّر حجمها' : 'drag or resize the circle'}
          </span>
          {visibleProperties.length > 0 && (
            <button
              type="button"
              onClick={() => setShowRegionList((v) => !v)}
              className={`flex h-8 w-8 items-center justify-center rounded-full text-base font-bold ring-1 transition ${showRegionList ? 'bg-violet-600 text-white ring-violet-600' : 'bg-white text-violet-700 ring-violet-300 hover:bg-violet-50'}`}
              aria-label={direction === 'rtl' ? 'عرض العقارات في المنطقة' : 'View properties in area'}
              aria-pressed={showRegionList}
              title={direction === 'rtl' ? 'عرض العقارات في المنطقة' : 'View properties in area'}
            >
              <i className={showRegionList ? 'ri-eye-fill' : 'ri-eye-line'}></i>
            </button>
          )}
          <button
            type="button"
            onClick={toggleRadius}
            className="rounded-full bg-violet-100 px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-200"
          >
            {direction === 'rtl' ? 'مسح' : 'Clear'}
          </button>
        </div>
      )}

      {/* Properties-in-region list, opened from the (i) badge button. Clicking a
          row selects that property's popup and pans the map to its pin. */}
      {showRegionList && currentRegion && visibleProperties.length > 0 && (
        <div
          className="absolute top-16 left-4 z-40 flex max-h-[60%] w-[min(20rem,calc(100%-2rem))] flex-col overflow-hidden rounded-2xl border border-violet-200 bg-white/95 shadow-2xl"
          dir={direction}
        >
          <div className="flex items-center justify-between border-b border-violet-100 px-4 py-2.5">
            <span className="text-sm font-semibold text-violet-900">
              {direction === 'rtl'
                ? `العقارات في المنطقة (${visibleProperties.length})`
                : `Properties in area (${visibleProperties.length})`}
            </span>
            <button
              type="button"
              onClick={() => setShowRegionList(false)}
              className="flex h-6 w-6 items-center justify-center rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200"
              aria-label={direction === 'rtl' ? 'إغلاق' : 'Close'}
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {visibleProperties.map((prop) => {
              const isActive = displayProperty?.id === prop.id;
              return (
                <button
                  key={prop.id}
                  type="button"
                  onClick={() => {
                    setHoveredProperty(null);
                    setClickedProperty(prop);
                    mapInstanceRef.current?.setCenter({ lat: prop.latitude, lng: prop.longitude });
                  }}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-start transition hover:bg-violet-50 ${isActive ? 'bg-violet-50' : ''}`}
                >
                  <img
                    src={prop.imageUrl}
                    alt=""
                    className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
                    loading="lazy"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-gray-900">
                      {getLocalizedField(prop, 'title', prop.title)}
                    </span>
                    <span className="block truncate text-xs text-gray-500">
                      {getLocalizedField(prop, 'city', prop.city)}
                    </span>
                  </span>
                  <span className="flex-shrink-0 text-sm font-bold text-[#004D40]">
                    {formatRiyalNumber(prop.price)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {displayProperty && (
        <div
          className={`absolute bottom-4 z-40 mx-auto max-w-md overflow-hidden rounded-2xl border border-white/40 shadow-2xl ${
            districtPanelOpen
              ? direction === 'rtl'
                ? 'left-4 right-4 md:right-[344px]'
                : 'left-4 right-4 md:left-[344px]'
              : 'left-4 right-4'
          }`}
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))',
            backdropFilter: 'blur(20px)',
          }}
          dir={direction}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={() => { setClickedProperty(null); setHoveredProperty(null); }}
            className="absolute top-2 right-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/30 text-white text-xs hover:bg-black/50 transition"
          >
            <i className="ri-close-line text-sm"></i>
          </button>

          {/* Prev / next paging through the visible properties. Arrows are
              physical (left/right edges); in RTL the visually-left arrow steps
              forward to match standard RTL carousel direction. */}
          {visibleProperties.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => navigateAdjacent(direction === 'rtl' ? 1 : -1)}
                className="absolute top-1/2 left-1 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-violet-700 shadow-lg ring-2 ring-violet-400 transition hover:bg-violet-50 hover:ring-violet-500"
                aria-label={direction === 'rtl' ? 'التالي' : 'Previous'}
              >
                <i className="ri-arrow-left-s-line text-2xl"></i>
              </button>
              <button
                type="button"
                onClick={() => navigateAdjacent(direction === 'rtl' ? -1 : 1)}
                className="absolute top-1/2 right-1 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-violet-700 shadow-lg ring-2 ring-violet-400 transition hover:bg-violet-50 hover:ring-violet-500"
                aria-label={direction === 'rtl' ? 'السابق' : 'Next'}
              >
                <i className="ri-arrow-right-s-line text-2xl"></i>
              </button>
              {displayIndex >= 0 && (
                <div
                  className="absolute top-2 left-2 z-10 rounded-full bg-black/40 px-2 py-0.5 text-[11px] font-medium text-white"
                  dir="ltr"
                >
                  {displayIndex + 1} / {visibleProperties.length}
                </div>
              )}
            </>
          )}
          <div className="flex items-stretch">
            {/* Property Image with Type Badge */}
            <div className="relative flex-shrink-0">
              <img
                src={displayProperty.imageUrl}
                alt={getLocalizedField(displayProperty, 'title', displayProperty.title)}
                className="h-full w-36 object-cover"
                style={{ minHeight: '180px' }}
                loading="lazy"
              />
              {/* Property Type Badge */}
              <div className="absolute top-3 left-3 rounded-full px-3 py-1 text-xs font-semibold text-white shadow-lg"
                   style={{ background: 'linear-gradient(135deg, #004D40, #1B7566)' }}>
                {getPropertyTypeLabel(displayProperty.propertyType)}
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col p-4">
              {/* Title & Location */}
              <h4 className="mb-1 line-clamp-2 font-bold text-gray-900" style={{ fontSize: '15px', lineHeight: '1.3' }}>
                {getLocalizedField(displayProperty, 'title', displayProperty.title)}
              </h4>
              <p className="mb-3 flex items-center gap-1 text-xs text-gray-500">
                <i className="ri-map-pin-line text-[#004D40]"></i>
                <span className="truncate">
                  {getLocalizedField(displayProperty, 'address', displayProperty.address)},{' '}
                  {getLocalizedField(displayProperty, 'city', displayProperty.city)}
                </span>
              </p>

              {/* Price Badge */}
              <div
                className="mb-3 inline-flex w-fit items-center gap-2 rounded-xl bg-[var(--pv-color-surface-sunken)] px-3 py-2"
                aria-label={displayPriceAccessible}
              >
                {/* The riyal symbol (in both languages), not the Arabic text «ر.س». */}
                <span className="flex h-8 w-8 items-center justify-center rounded-lg text-white"
                      style={{ background: '#004D40' }}>
                  <SaudiRiyalSymbol title="" className="h-4 w-4" />
                </span>
                <span className="text-lg font-bold text-[#004D40]">{displayPriceValue}</span>
              </div>

              {/* Property Stats — bedrooms / bathrooms only when the listing has them
                  (land, warehouses, studios … never show a "0"). */}
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                {displayProperty.bedrooms > 0 && (
                  <span className="flex items-center gap-1.5 rounded-lg bg-gray-100 px-2.5 py-1.5">
                    <BedroomIcon className="h-4 w-4 text-[#004D40]" aria-hidden />
                    {formatAmount(displayProperty.bedrooms)}
                  </span>
                )}
                {displayProperty.bathrooms > 0 && (
                  <span className="flex items-center gap-1.5 rounded-lg bg-gray-100 px-2.5 py-1.5">
                    <BathroomIcon className="h-4 w-4 text-[#004D40]" aria-hidden />
                    {formatAmount(displayProperty.bathrooms)}
                  </span>
                )}
                <span className="flex items-center gap-1.5 rounded-lg bg-gray-100 px-2.5 py-1.5">
                  <AreaIcon className="h-4 w-4 text-[#004D40]" aria-hidden />
                  {formatArea(displayProperty.sqft)} {unitLabel}
                </span>
              </div>

              {/* Broker Info */}
              <div className="mb-2 flex items-center gap-2">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #004D40, #1B7566)' }}>
                  {(displayProperty.agentName || 'A').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-gray-900 truncate">
                    {displayProperty.agentName || t('search.card.advertiser')}
                  </div>
                  {displayProperty.agentPhone && (
                    <div className="text-xs text-gray-500">{displayProperty.agentPhone}</div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-auto flex items-center gap-2">
                {displayProperty.agentPhone && (
                  <a
                    href={`tel:${displayProperty.agentPhone}`}
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-sky-500 text-white shadow-md transition-transform hover:scale-105"
                    title={t('search.map.call')}
                  >
                    <i className="ri-phone-fill text-lg"></i>
                  </a>
                )}
                {displayProperty.agentPhone && (
                  <a
                    href={`https://wa.me/${displayProperty.agentPhone.replace(/\D/g, '')}?text=${encodeURIComponent(getLocalizedField(displayProperty, 'title', displayProperty.title))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-white shadow-md transition-transform hover:scale-105"
                    style={{ background: '#25D366' }}
                    title="WhatsApp"
                  >
                    <i className="ri-whatsapp-fill text-lg"></i>
                  </a>
                )}

                {/* View Property Button */}
                <button
                  type="button"
                  onClick={() => handleMarkerClick(displayProperty)}
                  className="flex-1 cursor-pointer whitespace-nowrap rounded-xl py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl"
                  style={{ background: 'linear-gradient(135deg, #004D40, #1B7566)' }}
                >
                  {t('search.map.viewProperty')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* A region is drawn but nothing matching the filters falls inside it.
          Non-blocking (pointer-events-none) so the user can immediately redraw. */}
      {currentRegion && properties.length > 0 && visibleProperties.length === 0 && isMapReady && (
        <div
          className="pointer-events-none absolute left-1/2 top-20 z-30 max-w-[88%] -translate-x-1/2 rounded-2xl border border-amber-200 bg-white/95 px-4 py-3 text-center shadow-lg"
          dir={direction}
        >
          <p className="text-sm font-semibold text-amber-900">
            {direction === 'rtl'
              ? 'لا توجد عقارات مطابقة لفلاترك داخل هذه المنطقة'
              : 'No properties match your filters in this area'}
          </p>
          <p className="mt-0.5 text-xs text-amber-700">
            {direction === 'rtl'
              ? 'ارسم منطقة أخرى أو عدّل الفلاتر'
              : 'Draw another area or adjust your filters'}
          </p>
        </div>
      )}

      {properties.length === 0 && isMapReady && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/85">
          <div className="text-center">
            <i className="ri-map-2-line mb-4 text-4xl text-gray-400"></i>
            <p className="text-gray-600">{t('search.map.empty')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Extend window for global property selection handler
declare global {
  interface Window {
    selectProperty?: (propertyId: string) => void;
  }
}
